(() => {

  const translations = window.etTranslations || {};

  const STORAGE_KEY =
    'poseidn_cart_discount_code';

  let processing = false;
  let initialized = false;
  let revalidateTimer = null;
  let internalDiscountRequest = false;


  /* =========================================
     ELEMENTS
  ========================================= */

  function getElements() {

    return {

      input:
        document.getElementById(
          'CartDrawerDiscountCode'
        ),

      apply:
        document.getElementById(
          'CartDrawerDiscountApply'
        ),

      message:
        document.getElementById(
          'CartDrawerDiscountMessage'
        ),

      remove:
        document.getElementById(
          'CartDrawerDiscountRemove'
        ),

      summary:
        document.getElementById(
          'et-cart-discount-summary'
        ),

      discountCode:
        document.getElementById(
          'et-cart-discount-code'
        ),

      discountAmount:
        document.getElementById(
          'et-cart-discount-amount'
        ),

      subtotal:
        document.getElementById(
          'et-cart-subtotal'
        ),

      estimatedTotal:
        document.getElementById(
          'et-cart-estimated-total'
        ),

      checkout:
        document.getElementById(
          'nvd-with-checkout-btn'
        )
    };
  }


  /* =========================================
     CART URL
  ========================================= */

  function getCartUrl(
    endpoint
  ) {

    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {

      return (
        window.Shopify.routes.root +
        endpoint
      );
    }

    return `/${endpoint}`;
  }


  /* =========================================
     GET CART
  ========================================= */

  async function getAjaxCart() {

    const response =
      await fetch(
        getCartUrl('cart.js'),
        {
          method: 'GET',

          headers: {
            Accept:
              'application/json'
          },

          credentials: 'same-origin'
        }
      );

    if (!response.ok) {

      throw new Error(
        translations.unableLoad ||
        'Unable to load cart.'
      );
    }

    return response.json();
  }


  /* =========================================
     APPLY / REMOVE DISCOUNT
  ========================================= */

  async function updateCartDiscount(
    code
  ) {

    internalDiscountRequest =
      true;

    try {

      const response =
        await fetch(
          getCartUrl(
            'cart/update.js'
          ),
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            credentials:
              'same-origin',

            body:
              JSON.stringify({
                discount:
                  code || ''
              })
          }
        );

      if (!response.ok) {

        throw new Error(
          translations.unableUpdate ||
          'Unable to update cart discount.'
        );
      }

      return response.json();

    } finally {

      internalDiscountRequest =
        false;
    }
  }


  /* =========================================
     FORMAT MONEY
  ========================================= */

  function formatMoney(
    amount,
    currencyCode
  ) {

    return new Intl.NumberFormat(
      undefined,
      {
        style: 'currency',

        currency:
          currencyCode
      }
    ).format(
      Number(amount)
    );
  }


  /* =========================================
     MESSAGE
  ========================================= */

  function setMessage(
    text,
    type = ''
  ) {

    const {
      message
    } = getElements();

    if (!message) {
      return;
    }

    message.textContent =
      text;

    message.hidden =
      !text;

    message.className =
      `et-cart-discount__message ${type}`
        .trim();
  }


  /* =========================================
     GET LINE DISCOUNT TOTAL
  ========================================= */

  function getLineDiscountTotal(
    cart
  ) {

    if (
      !cart ||
      !Array.isArray(
        cart.items
      )
    ) {

      return 0;
    }

    return cart.items.reduce(
      (
        total,
        item
      ) => {

        return (
          total +
          Number(
            item.line_level_total_discount ||
            0
          )
        );

      },
      0
    );
  }


  /* =========================================
     CHECK WHETHER DISCOUNT APPLIED
  ========================================= */

  function discountWasApplied(
    beforeCart,
    afterCart,
    code
  ) {

    const normalizedCode =
      code
        .trim()
        .toLowerCase();


    /*
     * First check cart-level discount
     * applications.
     */
    const cartApplications =
      Array.isArray(
        afterCart
          .cart_level_discount_applications
      )
        ? afterCart
            .cart_level_discount_applications
        : [];


    for (
      let i = 0;
      i < cartApplications.length;
      i++
    ) {

      const application =
        cartApplications[i];

      const title =
        String(
          application?.title || ''
        )
          .trim()
          .toLowerCase();

      const applicationCode =
        String(
          application?.code || ''
        )
          .trim()
          .toLowerCase();

      if (
        title ===
          normalizedCode ||
        applicationCode ===
          normalizedCode
      ) {

        return true;
      }
    }


    /*
     * Check line-level discount
     * applications.
     */
    if (
      Array.isArray(
        afterCart.items
      )
    ) {

      for (
        let i = 0;
        i < afterCart.items.length;
        i++
      ) {

        const item =
          afterCart.items[i];

        const allocations =
          Array.isArray(
            item
              .line_level_discount_allocations
          )
            ? item
                .line_level_discount_allocations
            : [];

        for (
          let j = 0;
          j < allocations.length;
          j++
        ) {

          const allocation =
            allocations[j];

          const application =
            allocation
              ?.discount_application;

          const title =
            String(
              application?.title ||
              ''
            )
              .trim()
              .toLowerCase();

          const applicationCode =
            String(
              application?.code ||
              ''
            )
              .trim()
              .toLowerCase();

          if (
            title ===
              normalizedCode ||
            applicationCode ===
              normalizedCode
          ) {

            return true;
          }
        }
      }
    }


    /*
     * Finally compare total discount.
     *
     * This catches cases where Shopify doesn't
     * expose the code itself in the Ajax
     * response but the discount was actually
     * applied.
     */
    const beforeDiscount =
      Number(
        beforeCart?.total_discount ||
        0
      );

    const afterDiscount =
      Number(
        afterCart?.total_discount ||
        0
      );

    if (
      afterDiscount >
      beforeDiscount
    ) {

      return true;
    }


    return false;
  }


  /* =========================================
     BUILD DISCOUNT DATA
  ========================================= */

  function getDiscountData(
    beforeCart,
    afterCart,
    code
  ) {

    const applied =
      discountWasApplied(
        beforeCart,
        afterCart,
        code
      );

    if (!applied) {

      return null;
    }


    /*
     * Original subtotal before the promo.
     *
     * items_subtotal_price represents the
     * subtotal after line-level discounts,
     * before cart-level discounts.
     */
    const originalSubtotal =
      Number(
        beforeCart
          .items_subtotal_price ??
        beforeCart.total_price
      ) / 100;


    /*
     * The Ajax cart's subtotal after
     * line-level discounts.
     */
    const cartSubtotal =
      Number(
        afterCart
          .items_subtotal_price ??
        afterCart.total_price
      ) / 100;


    /*
     * Total discount BEFORE the code.
     */
    const beforeDiscount =
      Number(
        beforeCart.total_discount ||
        0
      );


    /*
     * Total discount AFTER the code.
     */
    const afterDiscount =
      Number(
        afterCart.total_discount ||
        0
      );


    /*
     * Additional discount created by
     * the promo code.
     */
    let discountAmount =
      Math.max(
        0,
        afterDiscount -
          beforeDiscount
      );


    /*
     * If the total discount delta is zero,
     * calculate from total price as a fallback.
     */
    if (
      discountAmount === 0
    ) {

      const beforeTotal =
        Number(
          beforeCart.total_price ||
          0
        );

      const afterTotal =
        Number(
          afterCart.total_price ||
          0
        );

      const totalDifference =
        Math.max(
          0,
          beforeTotal -
            afterTotal
        );

      /*
       * Only use this when it represents
       * an actual reduction.
       */
      if (
        totalDifference >
        0
      ) {

        discountAmount =
          totalDifference;
      }
    }


    /*
     * Final actual cart total.
     */
    const finalTotal =
      Number(
        afterCart.total_price ||
        0
      ) / 100;


    /*
     * Currency.
     */
    const currencyCode =
      afterCart.currency ||
      beforeCart.currency;


    return {

      code,

      originalSubtotal,

      cartSubtotal,

      discountAmount:
        discountAmount / 100,

      finalTotal,

      currencyCode
    };
  }


  /* =========================================
     SHOW DISCOUNT
  ========================================= */

  function showDiscount(
    discount
  ) {

    const {
      summary,
      discountCode,
      discountAmount,
      subtotal,
      estimatedTotal,
      remove
    } = getElements();

    if (!discount) {

      hideDiscount();

      return;
    }


    /*
     * Subtotal.
     */
    if (
      subtotal
    ) {

      subtotal.textContent =
        formatMoney(
          discount.originalSubtotal,
          discount.currencyCode
        );
    }


    /*
     * Promo code pill.
     */
    if (
      discountCode
    ) {

      discountCode.textContent =
        discount.code;
    }


    /*
     * Discount amount.
     */
    if (
      discountAmount
    ) {

      discountAmount.textContent =
        `- ${formatMoney(
          discount.discountAmount,
          discount.currencyCode
        )}`;
    }


    /*
     * Grand total.
     */
    if (
      estimatedTotal
    ) {

      estimatedTotal.textContent =
        formatMoney(
          discount.finalTotal,
          discount.currencyCode
        );
    }


    /*
     * Show summary.
     */
    if (
      summary
    ) {

      summary.hidden =
        false;
    }


    /*
     * Show X.
     */
    if (
      remove
    ) {

      remove.hidden =
        false;
    }


    setMessage('');
  }


  /* =========================================
     HIDE DISCOUNT
  ========================================= */

  function hideDiscount() {

    const {
      summary,
      discountCode,
      discountAmount,
      subtotal,
      remove
    } = getElements();


    if (
      summary
    ) {

      summary.hidden =
        true;
    }


    if (
      discountCode
    ) {

      discountCode.textContent =
        '';
    }


    if (
      discountAmount
    ) {

      discountAmount.textContent =
        '';
    }


    if (
      subtotal
    ) {

      subtotal.textContent =
        '';
    }


    if (
      remove
    ) {

      remove.hidden =
        true;
    }


    setMessage('');
  }


  /* =========================================
     REFRESH DISPLAY FROM ACTUAL CART
  ========================================= */

  async function refreshDiscountDisplay() {

    const savedCode =
      localStorage.getItem(
        STORAGE_KEY
      );

    if (!savedCode) {

      return;
    }


    try {

      const cart =
        await getAjaxCart();


      /*
       * If cart is empty, remove saved code.
       */
      if (
        !cart.items?.length
      ) {

        localStorage.removeItem(
          STORAGE_KEY
        );

        hideDiscount();

        return;
      }


      /*
       * We don't have the pre-discount cart
       * anymore, so use the stored original
       * subtotal as the baseline if available.
       */
      const savedOriginalSubtotal =
        Number(
          localStorage.getItem(
            `${STORAGE_KEY}_subtotal`
          )
        );


      const currentTotalDiscount =
        Number(
          cart.total_discount ||
          0
        ) / 100;


      const currentFinalTotal =
        Number(
          cart.total_price ||
          0
        ) / 100;


      const currentSubtotal =
        Number(
          cart.items_subtotal_price ??
          cart.total_price
        ) / 100;


      const {
        input,
        summary,
        discountCode,
        discountAmount,
        subtotal,
        estimatedTotal,
        remove
      } = getElements();


      if (
        input &&
        input.value.trim() === ''
      ) {

        input.value =
          savedCode;
      }


      if (
        discountCode
      ) {

        discountCode.textContent =
          savedCode;
      }


      if (
        subtotal
      ) {

        subtotal.textContent =
          formatMoney(
            Number.isFinite(
              savedOriginalSubtotal
            )
              ? savedOriginalSubtotal
              : currentSubtotal,
            cart.currency
          );
      }


      if (
        discountAmount
      ) {

        /*
         * We use the stored baseline when
         * possible.
         */
        const originalDiscount =
          Number(
            localStorage.getItem(
              `${STORAGE_KEY}_amount`
            )
          );


        if (
          Number.isFinite(
            originalDiscount
          )
        ) {

          discountAmount.textContent =
            `- ${formatMoney(
              Math.max(
                0,
                originalDiscount
              ),
              cart.currency
            )}`;

        } else {

          discountAmount.textContent =
            `- ${formatMoney(
              currentTotalDiscount,
              cart.currency
            )}`;
        }
      }


      if (
        estimatedTotal
      ) {

        estimatedTotal.textContent =
          formatMoney(
            currentFinalTotal,
            cart.currency
          );
      }


      if (
        summary
      ) {

        summary.hidden =
          false;
      }


      if (
        remove
      ) {

        remove.hidden =
          false;
      }

    } catch (
      error
    ) {

      console.error(
        'Discount display refresh error:',
        error
      );
    }
  }


  /* =========================================
     APPLY PROMO
  ========================================= */

  async function applyPromoCode() {

    if (
      processing
    ) {

      return;
    }


    const {
      input,
      apply
    } = getElements();


    if (
      !input ||
      !apply
    ) {

      return;
    }


    const code =
      input.value.trim();


    if (!code) {

      setMessage(
        translations.enterCode ||
          'Please enter a promo code.',
        'is-error'
      );

      hideDiscount();

      return;
    }


    processing =
      true;

    apply.disabled =
      true;


    const originalText =
      apply.textContent;


    apply.textContent = translations.applying || 'Applying...';


    setMessage('');


    try {

      /*
       * Get the REAL Shopify Ajax cart.
       */
      const beforeCart =
        await getAjaxCart();


      if (
        !beforeCart.items?.length
      ) {

        throw new Error(
          translations.cartEmpty ||
          'Your cart is empty.'
        );
      }


      /*
       * Apply discount directly to the
       * REAL cart.
       *
       * No Storefront API cart is created.
       */
      const afterCart =
        await updateCartDiscount(
          code
        );


      console.log(
        'ET native cart discount:',
        {
          code,

          beforeCart,

          afterCart,

          beforeTotal:
            beforeCart.total_price,

          afterTotal:
            afterCart.total_price,

          beforeDiscount:
            beforeCart.total_discount,

          afterDiscount:
            afterCart.total_discount,

          cartLevelApplications:
            afterCart
              .cart_level_discount_applications
        }
      );


      /*
       * Validate that Shopify actually
       * applied the code.
       */
      const discount =
        getDiscountData(
          beforeCart,
          afterCart,
          code
        );


      if (!discount) {

        /*
         * Shopify can return HTTP 200 even
         * when the code isn't applicable.
         */
        localStorage.removeItem(
          STORAGE_KEY
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_subtotal`
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_amount`
        );

        hideDiscount();


        setMessage(
          translations.invalidCode ||
            'This promo code is not valid for your cart.',
          'is-error'
        );


        /*
         * Remove any code that Shopify may
         * have partially accepted.
         */
        await updateCartDiscount(
          ''
        );


        return;
      }


      /*
       * Store the code.
       */
      localStorage.setItem(
        STORAGE_KEY,
        discount.code
      );


      /*
       * Store the original subtotal so
       * we can continue displaying it after
       * cart changes/page reload.
       */
      localStorage.setItem(
        `${STORAGE_KEY}_subtotal`,
        String(
          discount.originalSubtotal
        )
      );


      /*
       * Store the actual discount amount.
       */
      localStorage.setItem(
        `${STORAGE_KEY}_amount`,
        String(
          discount.discountAmount
        )
      );


      /*
       * Update our UI.
       */
      showDiscount(
        discount
      );


      /*
       * IMPORTANT:
       *
       * At this point /cart.js itself contains
       * the discount.
       *
       * BOGOS can now read the same cart.
       */
      console.log(
        'ET: Discount applied to actual Shopify cart.',
        {
          total:
            afterCart.total_price,

          totalDiscount:
            afterCart.total_discount
        }
      );


    } catch (
      error
    ) {

      console.error(
        'Promo error:',
        error
      );


      setMessage(
        error.message ||
          translations.unableValidate ||
          'Unable to validate promo code.',
        'is-error'
      );

    } finally {

      processing =
        false;

      apply.disabled =
        false;

      apply.textContent = translations.apply || 'Apply';

    }
  }


  /* =========================================
     REMOVE PROMO
  ========================================= */

  async function removePromoCode() {

    if (
      processing
    ) {

      return;
    }


    processing =
      true;


    const {
      input,
      remove,
      estimatedTotal
    } = getElements();


    if (
      remove
    ) {

      remove.disabled =
        true;
    }


    try {

      /*
       * Remove the discount from the REAL
       * Shopify Ajax cart.
       */
      const cart =
        await updateCartDiscount(
          ''
        );


      /*
       * Clear local state.
       */
      localStorage.removeItem(
        STORAGE_KEY
      );

      localStorage.removeItem(
        `${STORAGE_KEY}_subtotal`
      );

      localStorage.removeItem(
        `${STORAGE_KEY}_amount`
      );


      if (
        input
      ) {

        input.value =
          '';
      }


      hideDiscount();


      /*
       * Restore normal total.
       */
      if (
        estimatedTotal
      ) {

        estimatedTotal.textContent =
          formatMoney(
            Number(
              cart.total_price
            ) / 100,
            cart.currency
          );
      }


    } catch (
      error
    ) {

      console.error(
        'Remove promo error:',
        error
      );


      setMessage(
        error.message ||
          translations.unableRemove ||
          'Unable to remove promo code.',
        'is-error'
      );


    } finally {

      processing =
        false;


      if (
        remove
      ) {

        remove.disabled =
          false;
      }
    }
  }


  /* =========================================
     RESTORE PROMO
  ========================================= */

  async function restorePromo() {

    const savedCode =
      localStorage.getItem(
        STORAGE_KEY
      );


    if (!savedCode) {

      return;
    }


    const {
      input
    } = getElements();


    if (
      input
    ) {

      input.value =
        savedCode;
    }


    try {

      const cart =
        await getAjaxCart();


      if (
        !cart.items?.length
      ) {

        localStorage.removeItem(
          STORAGE_KEY
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_subtotal`
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_amount`
        );

        hideDiscount();

        return;
      }


      /*
       * Check whether the saved discount is
       * still actually present.
       */
      const savedSubtotal =
        Number(
          localStorage.getItem(
            `${STORAGE_KEY}_subtotal`
          )
        );


      const savedDiscount =
        Number(
          localStorage.getItem(
            `${STORAGE_KEY}_amount`
          )
        );


      /*
       * If we have no stored information,
       * re-apply the code to the real cart.
       */
      if (
        !Number.isFinite(
          savedSubtotal
        )
      ) {

        const beforeCart =
          await getAjaxCart();


        const afterCart =
          await updateCartDiscount(
            savedCode
          );


        const discount =
          getDiscountData(
            beforeCart,
            afterCart,
            savedCode
          );


        if (!discount) {

          localStorage.removeItem(
            STORAGE_KEY
          );

          hideDiscount();

          return;
        }


        localStorage.setItem(
          `${STORAGE_KEY}_subtotal`,
          String(
            discount.originalSubtotal
          )
        );


        localStorage.setItem(
          `${STORAGE_KEY}_amount`,
          String(
            discount.discountAmount
          )
        );


        showDiscount(
          discount
        );


        return;
      }


      /*
       * We already know the code was applied
       * previously. Show the current real
       * cart totals.
       */
      const {
        summary,
        discountCode,
        discountAmount,
        subtotal,
        estimatedTotal,
        remove
      } = getElements();


      if (
        summary
      ) {

        summary.hidden =
          false;
      }


      if (
        discountCode
      ) {

        discountCode.textContent =
          savedCode;
      }


      if (
        subtotal
      ) {

        subtotal.textContent =
          formatMoney(
            savedSubtotal,
            cart.currency
          );
      }


      if (
        discountAmount
      ) {

        discountAmount.textContent =
          `- ${formatMoney(
            Math.max(
              0,
              Number.isFinite(
                savedDiscount
              )
                ? savedDiscount
                : 0
            ),
            cart.currency
          )}`;
      }


      if (
        estimatedTotal
      ) {

        estimatedTotal.textContent =
          formatMoney(
            Number(
              cart.total_price
            ) / 100,
            cart.currency
          );
      }


      if (
        remove
      ) {

        remove.hidden =
          false;
      }


    } catch (
      error
    ) {

      console.error(
        'Restore promo error:',
        error
      );
    }
  }


  /* =========================================
     REVALIDATE AFTER CART CHANGE
  ========================================= */

  function scheduleRevalidation() {

    clearTimeout(
      revalidateTimer
    );


    revalidateTimer =
      setTimeout(
        () => {

          revalidatePromo();

        },
        700
      );
  }


  async function revalidatePromo() {

    const savedCode =
      localStorage.getItem(
        STORAGE_KEY
      );


    if (
      !savedCode ||
      processing
    ) {

      return;
    }


    try {

      const beforeCart =
        await getAjaxCart();


      if (
        !beforeCart.items?.length
      ) {

        localStorage.removeItem(
          STORAGE_KEY
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_subtotal`
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_amount`
        );

        hideDiscount();

        return;
      }


      /*
       * Re-apply the discount to the SAME
       * Shopify Ajax cart.
       */
      const afterCart =
        await updateCartDiscount(
          savedCode
        );


      const discount =
        getDiscountData(
          beforeCart,
          afterCart,
          savedCode
        );


      if (!discount) {

        localStorage.removeItem(
          STORAGE_KEY
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_subtotal`
        );

        localStorage.removeItem(
          `${STORAGE_KEY}_amount`
        );

        hideDiscount();


        setMessage(
          translations.noLongerValid ||
            'The promo code is no longer valid for your updated cart.',
          'is-error'
        );


        return;
      }


      /*
       * Update stored values.
       */
      localStorage.setItem(
        `${STORAGE_KEY}_subtotal`,
        String(
          discount.originalSubtotal
        )
      );


      localStorage.setItem(
        `${STORAGE_KEY}_amount`,
        String(
          discount.discountAmount
        )
      );


      showDiscount(
        discount
      );


    } catch (
      error
    ) {

      console.error(
        'Promo revalidation error:',
        error
      );
    }
  }


  /* =========================================
     WATCH CART REQUESTS
  ========================================= */

  function watchCartFetch() {

    const originalFetch =
      window.fetch;


    if (
      originalFetch.__etDiscountHooked
    ) {

      return;
    }


    async function patchedFetch(
      ...args
    ) {

      const request =
        args[0];


      const url =
        typeof request ===
          'string'
          ? request
          : request?.url ||
            '';


      const response =
        await originalFetch.apply(
          this,
          args
        );


      const isCartRequest =
        url.includes(
          '/cart/add.js'
        ) ||
        url.includes(
          '/cart/change.js'
        ) ||
        url.includes(
          '/cart/update.js'
        ) ||
        url.includes(
          '/cart/clear.js'
        );


      /*
       * Don't revalidate because of OUR OWN
       * discount update request.
       */
      if (
        isCartRequest &&
        !internalDiscountRequest
      ) {

        scheduleRevalidation();
      }


      return response;
    }


    patchedFetch.__etDiscountHooked =
      true;


    window.fetch =
      patchedFetch;
  }


  /* =========================================
     EVENTS
  ========================================= */

  function setupEvents() {

    const promoInput =
      document.getElementById(
        'CartDrawerDiscountCode'
      );


    const promoRemove =
      document.getElementById(
        'CartDrawerDiscountRemove'
      );


    /*
     * X button.
     */
    if (
      promoInput &&
      promoRemove
    ) {

      promoInput.addEventListener(
        'input',
        () => {

          promoRemove.hidden =
            promoInput.value.trim() === '';
        }
      );


      promoRemove.hidden =
        promoInput.value.trim() === '';
    }


    /*
     * Global click handling.
     *
     * No CSS selectors.
     */
    document.addEventListener(
      'click',
      (event) => {

        let element =
          event.target;


        if (
          !element ||
          element.nodeType !== 1
        ) {

          return;
        }


        /*
         * Walk up through parents.
         */
        while (
          element
        ) {

          /*
           * APPLY
           */
          if (
            element.id ===
            'CartDrawerDiscountApply'
          ) {

            event.preventDefault();

            applyPromoCode();

            return;
          }


          /*
           * REMOVE
           */
          if (
            element.id ===
            'CartDrawerDiscountRemove'
          ) {

            event.preventDefault();

            removePromoCode();

            return;
          }


          /*
           * Checkout is intentionally NOT
           * intercepted anymore.
           *
           * The discount now belongs to the
           * actual Shopify cart, so the normal
           * checkout flow should be used.
           */
          if (
            element.id ===
            'nvd-with-checkout-btn'
          ) {

            return;
          }


          element =
            element.parentElement;
        }
      },
      true
    );


    /*
     * Enter key.
     */
    document.addEventListener(
      'keydown',
      (event) => {

        const input =
          event.target;


        if (
          input?.id ===
            'CartDrawerDiscountCode' &&
          event.key ===
            'Enter'
        ) {

          event.preventDefault();

          applyPromoCode();
        }
      }
    );
  }


  /* =========================================
     Checkout Button Text Update
  ========================================= */
  function updateProtectedCheckoutText() {
    const subtotal =
      document.getElementById('nvd-subtotal');

    if (!subtotal) {
      return;
    }

    if (subtotal.textContent.includes('|')) {
      subtotal.textContent =
        translations.protectedCheckout ||
        'Protected checkout';
    }
  }

  function watchProtectedCheckout() {
    updateProtectedCheckoutText();

    const observer = new MutationObserver(() => {
      updateProtectedCheckoutText();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  watchProtectedCheckout();



  /* =========================================
     INIT
  ========================================= */

  function init() {

    if (
      initialized
    ) {

      return;
    }


    initialized =
      true;


    setupEvents();


    watchCartFetch();

    watchProtectedCheckout();


    /*
     * Restore after drawer exists.
     */
    setTimeout(
      restorePromo,
      500
    );


    setTimeout(
      restorePromo,
      1500
    );


    console.log(
      'ET cart discount initialized - native Ajax cart mode'
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init
    );

  } else {

    init();
  }




})();